import React from 'react';
import { OccupancySlot } from '../../types/venueBooking';
import { sectionsLabel } from '../../utils/venueSections';
import { GroupedSlot, addDays, groupSlots, hourRange, layoutLanes, sameDay, slotStyle, slotsOnDay, timeOf } from './occupancy';

interface WeeklyOccupancyGridProps {
  slots: OccupancySlot[];
  weekStart: Date;
  /** A nap fejlécére kattintva a napi nézet nyílik. */
  onDayClick: (date: Date) => void;
  onSlotClick: (group: GroupedSlot) => void;
  /** A részek nevei (név híján "N. rész") */
  sectionNames?: string[];
  sectionCount: number;
}

const HOUR_HEIGHT = 44; // px / óra
const DAY_SHORT = ['H', 'K', 'Sze', 'Cs', 'P', 'Szo', 'V'];

/**
 * A hét idővonala (asztali nézet): hét oszlop, a foglalások eseményenként egy
 * blokkban, a mai nap kiemelve. A blokkra kattintva a részletek, a nap
 * fejlécére kattintva a napi, részenkénti nézet nyílik.
 */
const WeeklyOccupancyGrid: React.FC<WeeklyOccupancyGridProps> = ({
  slots,
  weekStart,
  onDayClick,
  onSlotClick,
  sectionNames,
  sectionCount,
}) => {
  const [startHour, endHour] = hourRange(slots);
  const hours = Array.from({ length: endHour - startHour }, (_, i) => startHour + i);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = new Date();

  const position = (slot: OccupancySlot, day: Date): React.CSSProperties => {
    const s = new Date(slot.startTime);
    const e = new Date(slot.endTime);
    const startMin = sameDay(s, day) ? (s.getHours() - startHour) * 60 + s.getMinutes() : 0;
    const endMin = sameDay(e, day) ? (e.getHours() - startHour) * 60 + e.getMinutes() : (endHour - startHour) * 60;
    const top = Math.max(0, (startMin / 60) * HOUR_HEIGHT);
    return { top, height: Math.max(20, ((endMin - startMin) / 60) * HOUR_HEIGHT - 2) };
  };

  const cols = '3.5rem repeat(7, minmax(0, 1fr))';

  return (
    <div>
      <div className="grid border-b border-gray-200 bg-gray-50" style={{ gridTemplateColumns: cols }}>
        <div />
        {days.map((day, i) => {
          const isToday = sameDay(day, today);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onDayClick(day)}
              className="flex flex-col items-center gap-0.5 border-l border-gray-200 py-2 hover:bg-gray-100"
              title="Napi nézet, pályarészenként"
            >
              <span className={`text-[11px] font-semibold uppercase ${isToday ? 'text-primary-600' : 'text-gray-500'}`}>{DAY_SHORT[i]}</span>
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold tabular-nums ${
                  isToday ? 'bg-primary-600 text-white' : 'text-gray-900'
                }`}
              >
                {day.getDate()}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid" style={{ gridTemplateColumns: cols }}>
        <div>
          {hours.map((h) => (
            <div key={h} className="relative" style={{ height: HOUR_HEIGHT }}>
              <span className="absolute -top-2 right-2 bg-white px-0.5 text-[11px] tabular-nums text-gray-400">
                {h !== startHour && `${String(h).padStart(2, '0')}:00`}
              </span>
            </div>
          ))}
        </div>

        {days.map((day, i) => {
          const groups = groupSlots(slotsOnDay(slots, day));
          const lanes = layoutLanes(
            groups,
            (g) => new Date(g.slot.startTime).getTime(),
            (g) => new Date(g.slot.endTime).getTime()
          );
          return (
            <div
              key={i}
              className={`relative border-l border-gray-200 ${sameDay(day, today) ? 'bg-primary-50/40' : ''}`}
              style={{ height: hours.length * HOUR_HEIGHT }}
            >
              {hours.map((h) => (
                <div key={h} className="border-b border-gray-100" style={{ height: HOUR_HEIGHT }} />
              ))}

              {groups.map((group) => {
                const style = slotStyle(group.slot.type);
                const pos = position(group.slot, day);
                const { lane, lanes: laneCount } = lanes.get(group) ?? { lane: 0, lanes: 1 };
                return (
                  <button
                    key={`${group.slot.source}-${group.slot.id}`}
                    type="button"
                    onClick={() => onSlotClick(group)}
                    className={`absolute z-[5] flex overflow-hidden rounded-md text-left shadow-sm ring-1 transition hover:z-10 hover:shadow-md ${style.block}`}
                    style={{
                      ...pos,
                      left: `calc(${(lane / laneCount) * 100}% + 2px)`,
                      width: `calc(${100 / laneCount}% - 4px)`,
                    }}
                    title={`${group.slot.title} (${timeOf(group.slot.startTime)}–${timeOf(group.slot.endTime)})`}
                  >
                    <span className={`w-1 shrink-0 ${style.bar}`} />
                    <span className="min-w-0 px-1.5 py-0.5">
                      <span className="block truncate text-[11px] font-semibold leading-tight">{group.slot.title}</span>
                      {(pos.height as number) > 30 && (
                        <span className="block truncate text-[10px] leading-tight opacity-75">
                          {timeOf(group.slot.startTime)}
                          {sectionCount > 1 && group.sections !== null && ` · ${sectionsLabel(group.sections, sectionNames)}`}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeeklyOccupancyGrid;
